import React, { useContext } from 'react';
import Cookies from 'universal-cookie';
import { useRouter } from 'next/router';
import { stringify } from 'query-string';
import getConfig from 'next/config';

import { CustomerContext } from 'context/customer';

import CN_SIFT_SCIENCE_COOKIE from 'helpers/config';
import { addMatchingSavedAddressIdToLocalStorage } from 'helpers/localStorage';
import { loginMparticleUser, logMparticleEvent, mParticleEventTypes } from 'helpers/mParticle';
import siftPageView from 'helpers/siftScience';

const { publicRuntimeConfig } = getConfig();
const deployEnv = publicRuntimeConfig.deployEnvironment;
const googleKey = publicRuntimeConfig.googleOauthKey;

import { AuthModalType } from 'types/auth';

import { Auth } from '@chownow/cn-web-diner-auth';

interface AuthModalProps {
  activeAuthModal: AuthModalType;
  hideModal: () => {};
  email?: string;
  resetVerifyCode?: string;
}

export default function AuthModal({
  activeAuthModal,
  hideModal,
  email,
  resetVerifyCode,
}: AuthModalProps) {
  const { setUser, fetchUserProfile } = useContext(CustomerContext);
  const router = useRouter();

  // Update the URL to trigger keychain password saving behavior in Safari
  function updateLoginUrl() {
    const url = {
      search: stringify({ ...router.query, ...{ loginSuccess: true } }),
    };
    router.push(url);
  }

  // SiftScience is used to monitor fraud across sessions
  function handleSiftScience(): void {
    const cookies = new Cookies();
    const siftScienceCookie = cookies.get(CN_SIFT_SCIENCE_COOKIE);
    siftPageView(siftScienceCookie);
  }

  function handleLoadUser(data) {
    setUser(data);
    loginMparticleUser(data);
    handleSiftScience();
    updateLoginUrl();

    // Check to see if address in local storage is a saved address
    const savedAddresses = data?.delivery?.addresses;
    if (savedAddresses.length) {
      addMatchingSavedAddressIdToLocalStorage(savedAddresses);
    }
  }

  function onAuthSuccessHandler(data) {
    // if we do not have email, user is requesting verification code
    if (!data.email) return;

    // only load user when successful login
    if (data.id) {
      handleLoadUser(data);
    }
  }

  async function onCompleteHandler() {
    const data = await fetchUserProfile();

    handleLoadUser(data);

    hideModal();
  }

  function logAnalyticsEvent(eventData) {
    logMparticleEvent({
      ...eventData,
      eventType: mParticleEventTypes.Other,
    });
  }

  const { apiHost } = publicRuntimeConfig;

  return (
    <Auth
      authType={activeAuthModal}
      platform="brand"
      onAuthSuccess={onAuthSuccessHandler}
      onComplete={onCompleteHandler}
      apiHost={apiHost}
      deployEnv={deployEnv}
      AppleID={window.AppleID}
      GoogleAuth={window.google}
      googleKey={googleKey}
      logAnalyticsEvent={logAnalyticsEvent}
      email={email}
      resetVerifyCode={resetVerifyCode}
    />
  );
}
